// Remove once fields are moved to the shadow DOM
import './light-shadow.scss';

// Import dependencies
import { html, LitElement } from 'lit';
import { customElement, query, property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';

// Import global utils
import { isEmpty } from '@automator/shared/utils/is-empty';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { __, _x } from '@wordpress/i18n';

// Import html-entities
import { encode } from 'html-entities';

// Import definitions
import { getLoop } from '@automator/builder/definitions/items';

// Import recipe builder services
import { createLoop, updateLoop } from '@automator/builder/services/item/loop';

// eslint-disable-next-line require-jsdoc
@customElement( 'uap-builder-item-loop-edit-dialog' )
export class AutomatorBuilderItemLoopEditDialog extends LitElement {
	// Properties
	// The unique ID of the parent
	@property( { type: Number, attribute: 'parent-id' } ) accessor parentID;

	// The unique ID of the loop (parent of this loop)
	@property( { type: Number, attribute: 'loop-id' } ) accessor loopID;

	// Information about what has to be iterated (e.g. users, posts, tokens)
	@property( { type: String } ) accessor type;

	// The loop fields
	@property( { type: Object, attribute: false } ) accessor fields = {};

	// Returns the component of the sentence
	// We'll use this to save it as a backup for the logs
	@query( 'uap-builder-item-sentence' ) accessor $sentence;

	// Returns the component that has the fields of this loop
	// We'll use it to retrieve the values
	@query( 'uap-builder-item-legacy-form' ) accessor $optionsForm;

	/**
	 * Disable the shadow DOM
	 *
	 * @todo Remove this method once fields become web components. This component should be using the shadow DOM.
	 * @return {Element} The element
	 */
	createRenderRoot() {
		return this;
	}

	// eslint-disable-next-line require-jsdoc
	render() {
		return html`
			<uo-dialog
				heading=${ escapeHTML( __( 'Set data', 'uncanny-automator' ) ) }

				force-manual-close
				open

				@uap-dialog-close=${ this.handleDialogClose }
			>
				<div class="uap-loop-edit-dialog-header">
					<uap-builder-item-sentence
						item-id=${ this.loopID }
						.sentence=${ this.directoryLoopData.sentence }
						.fields=${ this.fields }
					></uap-builder-item-sentence>
				</div>

				<uap-builder-item-legacy-form
					parent-id=${ this.loopID }
					item-id=${ ifDefined( this.loopID ) }
					item-type="loop"

					.fieldsDirectory=${ this.directoryLoopData.fields }
					.fieldsValues=${ this.fields }

					return-format="loop"

					@uap-builder-legacy-form-change=${ ( event ) => {
						// Update the fields, even if they are not saved yet
						// The cancel button will revert the changes, and the save button will save them
						this.fields = event.detail.unsavedFieldValues;
					} }
				></uap-builder-item-legacy-form>

				${ this._templateDialogHelper }

				<uo-button
					slot="primary-action"

					@click=${ this.handleConfirmEdit }
				>
					${ escapeHTML( _x( 'Confirm', 'Verb', 'uncanny-automator' ) ) }
				</uo-button>

				<uo-button
					slot="secondary-action"
					dialog-action="close"
					color="secondary"
				>
					${ escapeHTML( _x( 'Cancel', 'Verb', 'uncanny-automator' ) ) }
				</uo-button>
			</uo-dialog>
		`;
	}

	/**
	 * Returns the template for the dialog helper
	 *
	 * @return {TemplateResult|undefined} The template
	 */
	get _templateDialogHelper() {
		if ( ! this.directoryLoopData.dialog_helper ) {
			return;
		}

		return html`
			<div class="uap-loop-edit-dialog-helper">
				${ this.directoryLoopData.dialog_helper }
			</div>
		`;
	}

	/**
	 * Handles updating the filter data on the server
	 *
	 * @return {Promise|undefined} The promise that either updates or creates the loop
	 */
	async handleConfirmEdit() {
		// Check if the fields are available
		if ( isEmpty( this.$optionsForm ) ) {
			return;
		}

		// Validate the form
		if ( ! this.$optionsForm.areFieldsValid ) {
			return;
		}

		// The loop already exists, just update it
		if ( this.mode === 'edit' ) {
			// Update data
			return updateLoop(
				this.loopID,
				{
					type: this.type,
					fields: this.fieldsForRemote,
					backup: this.backupForRemote
				}
			).then( response => {
				// Delete this component
				this.handleDialogClose();
			} );
		}

		// If the loop doesn't exist, create it and assign the current fields and backup
		if ( this.mode === 'create' ) {
			return createLoop( {
				type: this.type,
				fields: this.fieldsForRemote,
				backup: this.backupForRemote
			} ).then( response => {
				// Delete this component
				this.handleDialogClose();
			} );
		}
	}

	/**
	 * Returns the global/directory data of this loop
	 *
	 * @return {Object} The directory data of this loop. This contains, for example, the fields attributes
	 */
	get directoryLoopData() {
		return getLoop( this.type );
	}

	/**
	 * Creates the object that'll be saved in the database containing the values of the fields
	 *
	 * @return {Object|undefined} The fields
	 */
	get fieldsForRemote() {
		if ( ! isEmpty( this.$optionsForm ) ) {
			return JSON.stringify( this.$optionsForm.unsavedFieldValues );
		}
	}

	/**
	 * Creates the object that'll be saved in the database containing the backup
	 *
	 * @return {Object} The backup
	 */
	get backupForRemote() {
		return JSON.stringify( {
			// The sentence of the loop, without the values of the fields
			sentence: this.directoryLoopData.sentence,

			// The sentence with field values, HTML. Already encoded
			// eslint-disable-next-line camelcase
			sentence_html: encode( this.$sentence.outputForLogs )
		} )
	}

	/**
	 * Gets the mode of the dialog.
	 * If the `.loopID` is not defined, it means the loop doesn't exist yet and we're on "Edit" mode
	 *
	 * @return {String} The mode
	 */
	get mode() {
		return isEmpty( this.loopID ) ? 'create' : 'edit';
	}

	/**
	 * Delete this component when something closes the dialog
	 */
	handleDialogClose() {
		this.remove();
	}
}